import React, { useEffect } from 'react';
import { IStore } from "../../redux/reducers";
import { useSelector } from "react-redux";
import { Table, TableWrapper, Cell } from 'react-native-table-component';
import { View } from 'react-native';
import csv from 'csvtojson';
import { JobStatus } from '../../models';
import { tableStyle } from 'src/styles/tableStyle';
import { useColorModeValue, Box } from 'native-base';
import { useResponsive } from 'src/hooks/useResponsive';
import { IGrainWithProfiles } from 'src/models';
import { getText } from 'src/utils/i18n';
import { isElectron } from 'src/utils/miscUtils';
import { calculateRejectedWeightSummary } from 'src/utils/rejectConfig';
import { isLightboxFeatureEnabled, isRiceLikeGrain } from 'src/utils/grainFeatureUtils';

interface GrainTopLevelAnalysisTableProps {
  rejectionMapping?: Record<string, string>;
  rejectionCalculationConfig?: {
    rejectedGrains?: {
      selectedKeys?: string[];
      selectedLabels?: string[];
    };
  };
}

export default function GrainTopLevelAnalysisTable({
  rejectionMapping,
  rejectionCalculationConfig
}: GrainTopLevelAnalysisTableProps) {
  const { isLargeScreen } = useResponsive();
  const job = useSelector((store: IStore) => store.job.jobStatus.job);
  const jobStatus = useSelector((store: IStore) => store.job.currentJobStatus);
  const grains = useSelector((store: IStore) => store.user.userProfile.response?.grains) as
    | IGrainWithProfiles[]
    | undefined;
  const userSettings = useSelector((store: IStore) => store.user.userProfile.response?.settings);
  const lightboxEnabled = isLightboxFeatureEnabled(userSettings);
  const whitenessIndexEnabled = userSettings?.whiteness_index_enabled || false;
  const goodAndRejectPercentEnabled = userSettings?.good_and_reject_percent_enabled || false;
  const colorMode = useSelector((store: IStore) => store.settings.colorMode);
  const mainHeadBg = useColorModeValue('#a3b18a', '#4a5d47');
  const mainBorderColor = useColorModeValue('#94a3b8', '#718096');

  const [state, setState] = React.useState({
    tableHead: [getText('totalKernels')],
    tableData: [
      ['0'],
    ]
  });
  const electronApi = typeof window !== 'undefined'
    ? (window as unknown as {
        electron?: {
          fetchFileContent?: (url: string) => Promise<string>;
          readLocalFile?: (filePath: string) => Promise<string>;
        };
      }).electron
    : undefined;

  useEffect(() => {
    let cancelled = false;

    updateTopLevelAnalysis().catch((error) => {
      if (!cancelled) {
        console.error('Failed to update top-level analysis', error);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [job, jobStatus, grains, whitenessIndexEnabled, goodAndRejectPercentEnabled, rejectionCalculationConfig, rejectionMapping, lightboxEnabled]);

  const readCsvText = async (csvFileUrl: string) => {
    if (isElectron() && electronApi?.fetchFileContent) {
      return electronApi.fetchFileContent(csvFileUrl);
    }

    if (csvFileUrl.startsWith('file://')) {
      if (!isElectron() || !electronApi?.readLocalFile) {
        throw new Error('Local file access not available');
      }
      return electronApi.readLocalFile(csvFileUrl);
    }

    const response = await fetch(csvFileUrl);
    return response.text();
  };

  const updateTopLevelAnalysis = async () => {
    if (jobStatus != JobStatus.JOB_COMPLETED || !job) {
      return;
    }

    const head = [getText('totalKernels')];
    const result = [`${beautify(job.results?.grain_count)}`];

    const grainName = grains?.find((g) => g.grainId?.toString() === job.grainId?.toString())?.name;
    const isRice = isRiceLikeGrain(job.grainId, grainName);

    if (lightboxEnabled) {
      if (isRice) {
        head.push(getText('grainLength'));
        head.push(getText('grainWidth'));
        head.push(getText('brokenPercent'));
        result.push(`${beautify(job.results?.avg_length)}`);
        result.push(`${beautify(job.results?.avg_width)}`);
        result.push(`${beautify(job.results?.broken_percent ?? job.results?.rejection_percent)} %`);
      } else {
        head.push(getText('grainLength'));
        head.push(getText('grainArea'));
        result.push(`${beautify(job.results?.avg_length)}`);
        result.push(`${beautify(job.results?.avg_area_base)}`);
      }

      setState({
        tableHead: head,
        tableData: [result]
      });
      return;
    }

    if (whitenessIndexEnabled &&
      isRice
      && job.results?.whiteness_index !== undefined) {
      head.push(getText('whitenessIndex'));
      result.push(`${beautify(job.results?.whiteness_index)}`);
    }

    if (goodAndRejectPercentEnabled &&
      job.results?.good_grain_percent !== undefined &&
      job.results?.rejection_percent !== undefined) {
      let goodPercent = Number(job.results?.good_grain_percent);
      let rejectionPercent = Number(job.results?.rejection_percent);
      const rejectionCsvUrl = job.results?.rejection_analysis_display_csv || job.results?.rejection_analysis_csv;

      if (rejectionCsvUrl) {
        try {
          const csvText = await readCsvText(rejectionCsvUrl);
          const csvRows = await csv({
            noheader: true,
            output: "csv"
          }).fromString(csvText);
          const summary = calculateRejectedWeightSummary(
            csvRows[0] || [],
            csvRows.slice(1),
            rejectionCalculationConfig?.rejectedGrains,
            rejectionMapping ? { rejectionMapping } as any : undefined
          );
          if (summary?.filtered) {
            goodPercent = summary.goodPercent;
            rejectionPercent = summary.rejectionPercent;
          }
        } catch (error) {
          console.error('Failed to recalculate filtered rejection summary', error);
        }
      }

      head.push(getText('goodGrains'));
      head.push(getText('rejectedGrains'));
      result.push(`${beautify(goodPercent)} %`);
      result.push(`${beautify(rejectionPercent)} %`);
    }

    setState({
      tableHead: head,
      tableData: [result]
    });
  };

  const beautify = (data: number | string | undefined): string | number => {
    if (data === undefined || data === null) {
      return 0;
    }
    const numData = typeof data === 'string' ? parseFloat(data) : data;
    if (!isNaN(numData)) {
      if (Math.ceil(numData) === Math.floor(numData)) {
        return data;
      }
      return numData.toFixed(2);
    }
    return 0;
  };

  return (
    <Box mb={isLargeScreen ? 6 : 3}>
      <View style={isLargeScreen ? { paddingHorizontal: 12 } : {}}>
        <Table borderStyle={{ borderWidth: 1, borderColor: mainBorderColor }}>
          <TableWrapper style={[tableStyle.mainHead, { backgroundColor: mainHeadBg }]}>
            {
              state.tableHead.map((cellData, index) => (
                <Cell key={index} data={cellData} textStyle={[tableStyle.mainCellHead, { color: colorMode === 'dark' ? 'white' : 'black' }]} />
              )) as any
            }
          </TableWrapper>
          {
            state.tableData.map((rowData, index) => (
              <TableWrapper key={index} style={[tableStyle.mainRow, { backgroundColor: mainHeadBg }]}>
                {
                  rowData.map((cellData, cellIndex) => (
                    <Cell key={cellIndex} data={cellData} textStyle={[tableStyle.mainCellText, { color: colorMode === 'dark' ? 'white' : 'black' }]} />
                  )) as any
                }
              </TableWrapper>
            )) as any
          }
        </Table>
      </View>
    </Box>
  );
}
